import {
  Alert,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import type { ProvisionedEmailAccount } from "../../../api/emailAccountsTypes";
import { api } from "../../../api/operationsApi";
import { hasSmtpSettings } from "./smtpSettings";
import SmtpSettingsPanel from "./SmtpSettingsPanel";

interface Props {
  clientCode: string;
  onClose: (created: boolean) => void;
}

const createEmailAccount = withErrorHandling(api.emailAccounts.createEmailAccount);

const AddEmailAccountDialog = ({ clientCode, onClose }: Props) => {
  const getDomains = useCallback(() => api.emailAccounts.getClientEmailDomains(clientCode), [clientCode]);
  const [domains, domainsError] = useFetch(getDomains);
  const [displayName, setDisplayName] = useState("");
  const [localPart, setLocalPart] = useState("");
  const [domainName, setDomainName] = useState("");
  const [makeDefault, setMakeDefault] = useState(true);
  const [createSmtpLogin, setCreateSmtpLogin] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<ApiError>();
  const [created, setCreated] = useState<ProvisionedEmailAccount>();

  const handleCreateClick = async () => {
    setIsCreating(true);
    setCreateError(undefined);

    const [response, error] = await createEmailAccount(clientCode, {
      displayNameUserInput: displayName.trim(),
      localPart: localPart.trim(),
      domainName,
      makeDefault,
      createSmtpLogin,
    });
    setIsCreating(false);
    if (error) {
      logError(error, "[AddEmailAccountDialog] createEmailAccount");
      setCreateError(error);
      return;
    }

    setCreated(response);
  };

  const handleClose = () => {
    if (isCreating) {
      return;
    }

    onClose(created !== undefined);
  };

  const handleBackdropOrEscape = () => {
    const isOneTimePasswordShown = created !== undefined && hasSmtpSettings(created);
    if (isOneTimePasswordShown) {
      return;
    }

    handleClose();
  };

  if (domainsError) {
    logError(domainsError, "[AddEmailAccountDialog] getClientEmailDomains");
  }

  const canCreate = displayName.trim() !== "" && localPart.trim() !== "" && domainName !== "";

  return (
    <SmallDialog open onClose={handleBackdropOrEscape}>
      <DialogTitle>Add email account</DialogTitle>
      <DialogContent>
        {created && hasSmtpSettings(created) && <SmtpSettingsPanel account={created} />}
        {created && !hasSmtpSettings(created) && (
          <Alert severity="success">
            {created.address} was created for Platform sending. No SMTP login was created.
          </Alert>
        )}
        {!created && domainsError && <DataLoadingFailed text={domainsError.message} />}
        {!created && !domainsError && domains === undefined && <InlineLoader />}
        {!created && domains !== undefined && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Account name (sender name)"
              value={displayName}
              disabled={isCreating}
              onChange={(event) => setDisplayName(event.target.value)}
              autoFocus
            />
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <TextField
                label="Address"
                value={localPart}
                disabled={isCreating}
                onChange={(event) => setLocalPart(event.target.value)}
              />
              <Typography>@</Typography>
              <FormControl fullWidth>
                <InputLabel id="add-email-account-domain">Domain</InputLabel>
                <Select
                  labelId="add-email-account-domain"
                  label="Domain"
                  value={domainName}
                  disabled={isCreating || domains.domainNames.length === 0}
                  onChange={(event) => setDomainName(event.target.value)}
                >
                  {domains.domainNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={makeDefault}
                  disabled={isCreating}
                  onChange={(event) => setMakeDefault(event.target.checked)}
                />
              }
              label="Default sender for this client"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={createSmtpLogin}
                  disabled={isCreating}
                  onChange={(event) => setCreateSmtpLogin(event.target.checked)}
                />
              }
              label="Also create an SMTP login for Business Central"
            />
            {domains.domainNames.length === 0 && (
              <Alert severity="info">This client's organization has no active Mailgun domain.</Alert>
            )}
            {createError && <Alert severity="error">{createError.message}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogButtons onCancel={handleClose} cancelText="Close">
        {!created && (
          <Button variant="contained" loading={isCreating} disabled={!canCreate} onClick={handleCreateClick}>
            {createSmtpLogin ? "Create in Mailgun" : "Create"}
          </Button>
        )}
      </DialogButtons>
    </SmallDialog>
  );
};

export default AddEmailAccountDialog;
